import DirectApi from '../../../directApi';
import {CanSelect, contactKey, SelectableInvoice, SelectedInvoices} from './invoiceSelection';

export {contactKey};

/*
 * The Client match column of the import modal: whether each row's Xero contact matches an existing
 * Forecast client or will create one. The xero service answers per contact; the selection can then
 * turn an answer into review (two ticked contacts landing on one client, or two new contacts sharing
 * an email). Every function is pure except the fetch.
 * See docs/spec/invoicing/054-xero-import-client-match.md.
 */

/** Contacts per request: the xero service answers at most 200. */
export const MAX_CONTACTS_PER_REQUEST = 200;

export type ContactPair = {tenantId: string; contactId: string};

/** One contact's answer. emailKey is a company-scoped hash of the contact's email, never the email. */
export type ContactMatch =
	| (ContactPair & {
			match: 'matched';
			via: 'link' | 'email';
			clientId: number;
			clientName: string;
			emailKey: string | null;
	  })
	| (ContactPair & {match: 'newClient'; emailKey: string | null})
	| (ContactPair & {match: 'review' | 'unknown'});

/** Answers by contactKey, kept for one open of the modal. */
export type KnownMatches = ReadonlyMap<string, ContactMatch>;

/** What a row's match is once the selection is applied. Unknown and unanswered contacts have none. */
export type EffectiveMatch = 'matched' | 'newClient' | 'review';

/** What the Client match cell shows. */
export type MatchDisplay = {state: 'checking' | 'unknown' | 'review' | 'newClient'} | {state: 'matched'; clientName: string};

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const isText = (value: unknown): value is string => typeof value === 'string';
const emailKeyOf = (value: unknown): string | null | undefined =>
	value === undefined || value === null ? null : isText(value) ? value : undefined;

const parseMatch = (entry: unknown): ContactMatch | null => {
	if (!isObject(entry) || !isText(entry.tenantId) || !isText(entry.contactId)) {
		return null;
	}
	const pair = {tenantId: entry.tenantId, contactId: entry.contactId};
	const emailKey = emailKeyOf(entry.emailKey);
	switch (entry.match) {
		case 'matched':
			if (
				(entry.via !== 'link' && entry.via !== 'email') ||
				typeof entry.clientId !== 'number' ||
				!isText(entry.clientName) ||
				emailKey === undefined
			) {
				return null;
			}
			return {
				...pair,
				match: 'matched',
				via: entry.via,
				clientId: entry.clientId,
				clientName: entry.clientName,
				emailKey,
			};
		case 'newClient':
			return emailKey === undefined ? null : {...pair, match: 'newClient', emailKey};
		case 'review':
		case 'unknown':
			return {...pair, match: entry.match};
		default:
			return null;
	}
};

/** The answers in a xero/invoiceClientMatches body, or null when it is not one. Malformed entries are left out. */
export const parseContactMatches = (body: unknown): ContactMatch[] | null => {
	if (!isObject(body) || !Array.isArray(body.contacts)) {
		return null;
	}
	return body.contacts.map(parseMatch).filter((match): match is ContactMatch => match !== null);
};

/** The answers for up to 200 contacts, or null when they cannot be read. Never rejects. */
export const fetchInvoiceClientMatches = async (contacts: ContactPair[]): Promise<ContactMatch[] | null> => {
	try {
		const result: Response = await DirectApi.Fetch_Raw_With_Init('xero/invoiceClientMatches', {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify({contacts}),
		});
		if (result.status !== 200) {
			return null;
		}
		return parseContactMatches(await result.json());
	} catch {
		return null;
	}
};

/** An answer kept for the open: matched, newClient or review. Unknown is asked again. */
export const isKept = (answer: ContactMatch | undefined): boolean => answer !== undefined && answer.match !== 'unknown';

/**
 * The distinct contacts of these rows with no kept answer, in row order: unanswered and unknown ones.
 * A row without a contact is never asked (the xero service refuses a blank id) and stays unknown.
 */
export const contactsToAsk = (rows: readonly SelectableInvoice[], known: KnownMatches, max = Infinity): ContactPair[] => {
	const seen = new Set<string>();
	const contacts: ContactPair[] = [];
	for (const row of rows) {
		if (contacts.length >= max) {
			break;
		}
		const key = contactKey(row);
		if (row.contactId.trim() === '' || seen.has(key) || isKept(known.get(key))) {
			continue;
		}
		seen.add(key);
		contacts.push({tenantId: row.tenantId, contactId: row.contactId});
	}
	return contacts;
};

const addTo = (groups: Map<string, string[]>, group: string, key: string): void => {
	groups.set(group, [...(groups.get(group) ?? []), key]);
};

/**
 * Every answered contact's match, with the selection applied (spec D4). Among the ticked contacts:
 * two or more new contacts sharing an emailKey are all review; two or more contacts on one client are
 * review, except one matched by link, which is certain and stays matched.
 */
export const effectiveMatches = (known: KnownMatches, selected: SelectedInvoices): Map<string, EffectiveMatch> => {
	const effective = new Map<string, EffectiveMatch>();
	known.forEach((answer, key) => {
		if (answer.match !== 'unknown') {
			effective.set(key, answer.match);
		}
	});

	const ticked = new Set<string>();
	selected.forEach(row => ticked.add(contactKey(row)));
	const newByEmail = new Map<string, string[]>();
	const onClient = new Map<string, string[]>();
	ticked.forEach(key => {
		const answer = known.get(key);
		if (answer?.match === 'newClient' && answer.emailKey !== null) {
			addTo(newByEmail, answer.emailKey, key);
		} else if (answer?.match === 'matched') {
			addTo(onClient, String(answer.clientId), key);
		}
	});

	newByEmail.forEach(keys => {
		if (keys.length > 1) {
			keys.forEach(key => effective.set(key, 'review'));
		}
	});
	onClient.forEach(keys => {
		if (keys.length > 1) {
			keys.forEach(key => {
				const answer = known.get(key);
				if (answer?.match === 'matched' && answer.via === 'email') {
					effective.set(key, 'review');
				}
			});
		}
	});
	return effective;
};

/** A copy of the row carrying its effective match, as the selection and Create read it. */
export const decorate = (row: SelectableInvoice, effective: ReadonlyMap<string, EffectiveMatch>): SelectableInvoice => ({
	...row,
	clientMatch: effective.get(contactKey(row)),
});

/** What the row's Client match cell shows. */
export const matchDisplay = (
	row: SelectableInvoice,
	known: KnownMatches,
	effective: ReadonlyMap<string, EffectiveMatch>
): MatchDisplay => {
	if (row.contactId.trim() === '') {
		return {state: 'unknown'};
	}
	const key = contactKey(row);
	const answer = known.get(key);
	if (answer === undefined) {
		return {state: 'checking'};
	}
	const match = effective.get(key);
	if (match === undefined) {
		return {state: 'unknown'};
	}
	if (match === 'matched' && answer.match === 'matched') {
		return {state: 'matched', clientName: answer.clientName};
	}
	return {state: match === 'matched' ? 'unknown' : match};
};

/**
 * Who may tick a row (AC 8): with CLIENT_CREATE, anyone. Without it, only a row whose contact's own
 * answer is matched: a new client, review or unknown row could still end up creating a client.
 */
export const canSelectFor =
	(hasClientCreate: boolean, known: KnownMatches): CanSelect =>
	row =>
		hasClientCreate || known.get(contactKey(row))?.match === 'matched';
